import React, {
  createContext,
  useEffect,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import Honeybadger from '@honeybadger-io/js';
import RegistryItemGatewayApi from '../services/RegistryItemGatewayApi';
import {RETAILER} from '../constants/storeTypes';
import getLinkedRegistryByRetailer from '../utils/getLinkedRegistryByRetailer';
import CoupleContext from './CoupleContext';
import MemberContext from './MemberContext';
import RetailersContext from './RetailersContext';
import {TRACKING_API_HOST} from '../constants';
import noop from '../utils/noop';

const CLICK_SOURCE_ID = 'C15CE921-6ECA-49E4-BDDE-2CFF60D2DCC0';

const getTrackingUrlByRetailerId = (retailerId, retailers, registries) => {
  const retailer = retailers.find(r => r.id === retailerId);
  if (!retailer || retailer.isDeleted || !retailer.isPartner) return null;

  const linkedRegistry = getLinkedRegistryByRetailer(registries, retailer);

  const trackingUrl = linkedRegistry
    ? `${TRACKING_API_HOST}/track/manage/${CLICK_SOURCE_ID}?rt=${retailer.id}&r=${linkedRegistry.id}`
    : `${TRACKING_API_HOST}/track/create/${CLICK_SOURCE_ID}?rt=${retailer.id}`;

  return trackingUrl;
};

const mapGiftsByStore = (giftsByStore, registries, retailers) => {
  return giftsByStore
    .map(group => ({
      ...group,
      trackingUrl:
        group.type === RETAILER
          ? getTrackingUrlByRetailerId(group.retailerId, retailers, registries)
          : null,
    }))
    .filter(group => group.trackingUrl);
};

const Context = createContext({
  fetchSummary: noop,
  isFetching: true,
  summary: {},
  guestCount: null,
  updateSummaryWithCashFund: noop,
});

export default Context;

export const SummaryContextProvider = ({children}) => {
  const [rawSummary, setRawSummary] = useState({});
  const [isFetching, setIsFetching] = useState(true);
  const {registries} = useContext(CoupleContext);
  const {retailers} = useContext(RetailersContext);
  const {member} = useContext(MemberContext);

  const fetchSummary = useCallback(async () => {
    try {
      setIsFetching(true);

      const summaryResponse = await RegistryItemGatewayApi.getSummary({
        priceRanges: '0-4999,5000-9999,10000-14999,15000-*',
      });

      setRawSummary(summaryResponse);

      setIsFetching(false);
    } catch (error) {
      setIsFetching(false);
      setRawSummary({});

      Honeybadger.notify(error, 'GiftsSummaryError');
    }
  }, []);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  const summary = useMemo(() => {
    if (rawSummary.giftsByStore) {
      return {
        ...rawSummary,
        giftsByStore: mapGiftsByStore(
          rawSummary.giftsByStore,
          registries,
          retailers,
        ),
      };
    }
    return rawSummary;
  }, [rawSummary, registries, retailers]);

  const guestCount = member?.number_of_guests;

  const updateSummaryWithCashFund = newCashFund => {
    if (summary) {
      const updatedSummary = {...summary};
      updatedSummary.cashFunds.push(newCashFund);
      updatedSummary.totalCashFunds += 1;

      setRawSummary(updatedSummary);
    }
  };

  return (
    <Context.Provider
      value={{
        fetchSummary,
        isFetching,
        summary,
        guestCount,
        updateSummaryWithCashFund,
      }}>
      {children}
    </Context.Provider>
  );
};

SummaryContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
